import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Button,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  useTheme,
  alpha,
  Fade,
  Alert,
  AlertTitle,
  Grid,
} from "@mui/material";
import { useState } from "react";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import usePaylat from "../../hooks/usePayLater";
import Footer from "@/layouts/footer";
import NavPage from "@/layouts/nav-header";
import {
  CreditCard,
  CheckCircle,
  Send,
  Business,
  Person,
  Email,
  Phone,
} from "@mui/icons-material";

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Invalid email").min(1, "Email is required"),
  phoneNumber: z
    .string()
    .min(10, "Phone number must be at least 10 digits")
    .max(10)
    .optional(),
  company: z.string().min(1, "Company is required"),
});

type FormData = z.infer<typeof formSchema>;

function PayLater() {
  const { sendPaylat, isLoading, error } = usePaylat();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const theme = useTheme();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phoneNumber: "",
      company: "",
    },
  });

  const onSubmit = async (data: FormData): Promise<void> => {
    const response = await sendPaylat(data);
    if (response) {
      setIsSubmitted(true);
    }
  };

  const { locale } = useLocale();
  const { t } = useTranslate(locale);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "grey.50",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <NavPage isForm={1}>
        <Box sx={{ mt: { xs: 8, md: 12 }, mb: 6, px: 2 }}>
          <Fade in={true}>
            <Box sx={{ maxWidth: 800, mx: "auto" }}>
              {/* Header Section */}
              <Box sx={{ textAlign: "center", mb: 6 }}>
                <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CreditCard color="primary" sx={{ fontSize: 40 }} />
                  </Box>
                </Box>
                <Typography
                  variant="h3"
                  fontWeight={600}
                  color="text.primary"
                  gutterBottom
                >
                  {t("payLater.title")}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ maxWidth: 600, mx: "auto" }}
                >
                  {t("payLater.content")}
                </Typography>
              </Box>

              {/* Success Message */}
              {isSubmitted && (
                <Fade in={true}>
                  <Card
                    elevation={2}
                    sx={{
                      borderRadius: 3,
                      border: `1px solid ${theme.palette.success.main}`,
                      bgcolor: alpha(theme.palette.success.main, 0.05),
                    }}
                  >
                    <CardContent sx={{ p: 4, textAlign: "center" }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          mb: 2,
                        }}
                      >
                        <CheckCircle color="success" sx={{ fontSize: 48 }} />
                      </Box>
                      <Typography
                        variant="h6"
                        color="success.main"
                        fontWeight={600}
                        gutterBottom
                      >
                        {t("payLater.successMessage")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        We will contact you within 24-48 hours to activate the
                        service.
                      </Typography>
                    </CardContent>
                  </Card>
                </Fade>
              )}

              {/* Form */}
              {!isSubmitted && (
                <Fade in={true}>
                  <Card
                    elevation={2}
                    sx={{
                      borderRadius: 3,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Typography
                        variant="h5"
                        fontWeight={600}
                        sx={{ mb: 3, textAlign: "center" }}
                      >
                        Request Pay Later Service
                      </Typography>

                      <Box
                        component="form"
                        onSubmit={form.handleSubmit(onSubmit)}
                      >
                        <Grid container spacing={3}>
                          {/* Name Field */}
                          <Grid item xs={12} md={6}>
                            <TextField
                              {...form.register("name")}
                              label={t("item.fields.name")}
                              placeholder={t("item.fields.name")}
                              fullWidth
                              variant="outlined"
                              error={!!form.formState.errors.name}
                              helperText={form.formState.errors.name?.message}
                              InputProps={{
                                startAdornment: (
                                  <Person color="action" sx={{ mr: 1 }} />
                                ),
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                },
                              }}
                            />
                          </Grid>

                          {/* Email Field */}
                          <Grid item xs={12} md={6}>
                            <TextField
                              {...form.register("email")}
                              label={t("profile.fields.email")}
                              placeholder={t("profile.fields.email")}
                              type="email"
                              fullWidth
                              variant="outlined"
                              error={!!form.formState.errors.email}
                              helperText={form.formState.errors.email?.message}
                              InputProps={{
                                startAdornment: (
                                  <Email color="action" sx={{ mr: 1 }} />
                                ),
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                },
                              }}
                            />
                          </Grid>

                          {/* Phone Number Field */}
                          <Grid item xs={12} md={6}>
                            <TextField
                              {...form.register("phoneNumber")}
                              label={t("profile.fields.phoneNumber")}
                              placeholder={t("profile.fields.phoneNumber")}
                              fullWidth
                              variant="outlined"
                              error={!!form.formState.errors.phoneNumber}
                              helperText={
                                form.formState.errors.phoneNumber?.message
                              }
                              InputProps={{
                                startAdornment: (
                                  <Phone color="action" sx={{ mr: 1 }} />
                                ),
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                },
                              }}
                            />
                          </Grid>

                          {/* Company Field */}
                          <Grid item xs={12} md={6}>
                            <TextField
                              {...form.register("company")}
                              label={t("company.info")}
                              placeholder={t("company.info")}
                              fullWidth
                              variant="outlined"
                              error={!!form.formState.errors.company}
                              helperText={
                                form.formState.errors.company?.message
                              }
                              InputProps={{
                                startAdornment: (
                                  <Business color="action" sx={{ mr: 1 }} />
                                ),
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                },
                              }}
                            />
                          </Grid>
                        </Grid>

                        {/* Error Alert */}
                        {error && (
                          <Alert
                            severity="error"
                            sx={{ mt: 3, borderRadius: 2 }}
                          >
                            <AlertTitle>Error</AlertTitle>
                            {t("payLater.errorMessage")}
                          </Alert>
                        )}

                        {/* Submit Button */}
                        <Box sx={{ mt: 4, textAlign: "center" }}>
                          <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            disabled={isLoading}
                            startIcon={
                              isLoading ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                <Send />
                              )
                            }
                            sx={{
                              borderRadius: 2,
                              px: 4,
                              py: 1.5,
                              textTransform: "none",
                              fontWeight: 600,
                              fontSize: "1.1rem",
                            }}
                          >
                            {isLoading
                              ? "Sending Request..."
                              : t("payLater.submit")}
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              )}

              {/* Benefits Section */}
              {!isSubmitted && (
                <Fade in={true}>
                  <Box sx={{ mt: 6 }}>
                    <Typography
                      variant="h5"
                      fontWeight={600}
                      sx={{ mb: 4, textAlign: "center" }}
                    >
                      Why Choose Pay Later?
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <Card
                          elevation={1}
                          sx={{
                            borderRadius: 3,
                            border: `1px solid ${theme.palette.divider}`,
                            height: "100%",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              elevation: 4,
                              transform: "translateY(-2px)",
                            },
                          }}
                        >
                          <CardContent sx={{ p: 3, textAlign: "center" }}>
                            <Typography
                              variant="h6"
                              fontWeight={600}
                              sx={{ mb: 2 }}
                            >
                              Flexible Payment Options
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Choose from multiple payment methods that suit
                              your business needs
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Card
                          elevation={1}
                          sx={{
                            borderRadius: 3,
                            border: `1px solid ${theme.palette.divider}`,
                            height: "100%",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              elevation: 4,
                              transform: "translateY(-2px)",
                            },
                          }}
                        >
                          <CardContent sx={{ p: 3, textAlign: "center" }}>
                            <Typography
                              variant="h6"
                              fontWeight={600}
                              sx={{ mb: 2 }}
                            >
                              Extended Payment Terms
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Get up to 90 days to pay with our flexible payment
                              solutions
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Card
                          elevation={1}
                          sx={{
                            borderRadius: 3,
                            border: `1px solid ${theme.palette.divider}`,
                            height: "100%",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              elevation: 4,
                              transform: "translateY(-2px)",
                            },
                          }}
                        >
                          <CardContent sx={{ p: 3, textAlign: "center" }}>
                            <Typography
                              variant="h6"
                              fontWeight={600}
                              sx={{ mb: 2 }}
                            >
                              Secure Transactions
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Bank-level security ensures your financial
                              information is protected
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Box>
                </Fade>
              )}
            </Box>
          </Fade>
        </Box>
        <Footer />
      </NavPage>
    </Box>
  );
}

export default PayLater;
